import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { ArrowBackIos } from '@mui/icons-material';
import { Alert, IconButton, Stack, Typography } from '@mui/material';
import { isAxiosError } from 'axios';
import { MuiOtpInput } from 'mui-one-time-password-input';
import { useDebouncedCallback } from 'use-debounce';

import { LocalizationTypography } from './localization';

import { STEP, TRACKING_BUTTON } from '~common';
import { MainButton } from '~components/atoms';
import { useEventTrackingContext, useMainContext } from '~providers';
import { formatNumber } from '~utils/phone.util';

export const StepTwo = () => {
  const { recordBtnClicked } = useEventTrackingContext();
  const {
    phoneNumber,
    login,
    isLoadingLogin,
    isLoadingOTP,
    changeStep,
    handleSubmitGetOTP,
    sendOTPFailedError,
    setSendOTPFailedError,
  } = useMainContext();
  const [countdownTime, setCountdownTime] = useState(30);
  const [intervalId, setIntervalId] = useState<NodeJS.Timer>();
  const [isLoginFailed, setIsLoginFailed] = useState(false);
  const [isTooManyAttempts, setIsTooManyAttempts] = useState(false);
  const [otpCode, setOTPCode] = useState<string>('');

  const firstMuiOtpInputRef = useRef<HTMLInputElement | null>();

  const onChangeOTPCode = (value: string) => {
    setOTPCode(value);
    setIsLoginFailed(false);
    setIsTooManyAttempts(false);
    setSendOTPFailedError(null);
  };

  const onLoginClick = useCallback(async () => {
    try {
      recordBtnClicked(TRACKING_BUTTON.CONFIRM_VERIFICATION_CODE);
      await login(otpCode);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 429) {
        setIsTooManyAttempts(true);
      } else {
        setIsLoginFailed(true);
      }
      setOTPCode('');
      firstMuiOtpInputRef.current?.focus();
    }
  }, [login, otpCode, recordBtnClicked]);

  /**
   * Please note that there may be pending requests after unmount.
   * Currently, these requests will be automatically canceled by the library
   */
  const debouncedLoginOnComplete = useDebouncedCallback(onLoginClick, 700);

  const startCountDown = () => {
    setCountdownTime(30);
    setIsLoginFailed(false);
    setIsTooManyAttempts(false);
    setSendOTPFailedError(null);
    setIntervalId(
      setInterval(function () {
        setCountdownTime((prevCountdownTime) => prevCountdownTime - 1);
      }, 1000),
    );
  };

  function handleResendOTPClick() {
    handleSubmitGetOTP();
    startCountDown();
    recordBtnClicked(TRACKING_BUTTON.RESEND_VERIFICATION_CODE);
  }

  useEffect(() => {
    if (countdownTime === 0 && intervalId) {
      // Enable the resend OTP button
      clearInterval(intervalId);
    }
  }, [countdownTime, intervalId]);

  useEffectOnce(() => {
    startCountDown();
  });

  return (
    <>
      <Stack display="grid" gap={1}>
        <Stack flexDirection="row">
          <IconButton
            aria-label="back"
            size="small"
            color="inherit"
            sx={{ paddingY: 0 }}
            onClick={() => changeStep(STEP.INPUT_PHONE_NUMBER)}
          >
            <ArrowBackIos />
          </IconButton>
          <LocalizationTypography
            variant="Poppins/Heading-3"
            textId="app.rentalPage.stepTwo.enterCode"
            defaultText="Enter verification code"
          />
        </Stack>
        <Stack direction="row" gap={0.4} flexWrap={'wrap'}>
          <LocalizationTypography
            variant="Inter/Body-sm"
            color="#8D8D8D"
            textId="app.rentalPage.stepTwo.codeSentTo"
            defaultText="We sent a verification code to"
          />
          <Typography variant="Inter/Body-sm" color="primary.main">
            {formatNumber(phoneNumber?.replace(/\D/g, ''))}
          </Typography>
        </Stack>
      </Stack>

      <Stack alignItems="center" gap={0.5}>
        <MuiOtpInput
          justifyContent="center"
          sx={{
            gap: 0,
            paddingX: 1.4375,
            '&> div:not(:last-of-type)': {
              marginRight: 1,
            },
            '& .MuiInputBase-input': {
              padding: 2.5,
              fontSize: '1.5rem',
              caretColor: (theme) => `${theme.palette.primary.main}`,
            },
            '& .MuiInputBase-root': {
              boxShadow: 'none',
            },
            '& .MuiInputBase-root.MuiFilledInput-root.Mui-focused': {
              borderColor: (theme) => `${theme.palette.primary.main}`,
              boxShadow: (theme) => `${theme.palette.primary.main} 0 0 0 2px`,
            },
            '& .MuiInputBase-root.Mui-error:not(.Mui-focused)': {
              boxShadow: 'none',
              backgroundColor: (theme) => theme.palette.error.light,
              transition: 'none',
            },
          }}
          TextFieldsProps={{
            type: 'number',
            error: isLoginFailed || isTooManyAttempts,
            inputProps: {
              ref: (element: HTMLInputElement) => {
                if (!firstMuiOtpInputRef.current) {
                  firstMuiOtpInputRef.current = element;
                }
              },
              inputMode: 'numeric',
              pattern: '[0-9]*',
            },
          }}
          inputMode="numeric"
          autoFocus
          value={otpCode}
          onChange={onChangeOTPCode}
          onComplete={debouncedLoginOnComplete}
        />
        {isLoginFailed && (
          <Alert severity="error" sx={{ p: 0 }}>
            <LocalizationTypography
              variant="Inter/Body-sm"
              color="error"
              textId="text.loginFailed"
              defaultText="Invalid code. Please try again."
            />
          </Alert>
        )}

        {isTooManyAttempts && (
          <Alert severity="error" sx={{ p: 0 }}>
            <LocalizationTypography
              variant="Inter/Body-sm"
              color="error"
              textId="text.tooManyAttempts"
              defaultText="Too many attempts. Please try again later."
            />
          </Alert>
        )}

        {sendOTPFailedError && (
          <Alert severity="error" sx={{ p: 0 }}>
            <LocalizationTypography
              variant="Inter/Body-sm"
              color="error"
              textId={sendOTPFailedError}
              defaultText="Failed to send code. Please try again."
            />
          </Alert>
        )}
      </Stack>

      <Stack gap={2.5} alignItems="center" sx={{ mt: 1 }}>
        <MainButton
          fullWidth
          disabled={
            !otpCode ||
            otpCode?.length !== 4 ||
            isLoadingLogin ||
            debouncedLoginOnComplete.isPending() ||
            isTooManyAttempts
          }
          variant="contained"
          onClick={onLoginClick}
          isLoading={isLoadingLogin || debouncedLoginOnComplete.isPending()}
        >
          <LocalizationTypography
            variant="Poppins/Subheading-md"
            textId="app.rentalPage.stepTwo.button.continue"
            defaultText="Continue"
          />
        </MainButton>

        <MainButton
          isLoading={isLoadingOTP}
          disabled={countdownTime > 0 || isLoadingOTP}
          variant="text"
          sx={{ padding: 0 }}
          onClick={handleResendOTPClick}
        >
          {countdownTime > 0 ? (
            <LocalizationTypography
              variant="Inter/Subheading-md"
              textId="app.rentalPage.stepTwo.button.resendInProgress"
              defaultText={`Resend Code in ${countdownTime}s`}
              textValues={{ value: countdownTime }}
            />
          ) : (
            <LocalizationTypography
              variant="Inter/Subheading-md"
              textId="app.rentalPage.stepTwo.button.resend"
              defaultText="Resend Code"
            />
          )}
        </MainButton>
      </Stack>
    </>
  );
};
